import type { Module } from '@contexts/modules-provider';

import swirlBottomRight from '@assets/images/selector-backgrounds/swirl-bottom-right.svg';
import swirlTopLeft from '@assets/images/selector-backgrounds/swirl-top-left.svg';
import { CobaltButton } from '@cae/cobalt-react/components/button';
import { CobaltClickableTile } from '@cae/cobalt-react/components/tile';
import { MediaQuery } from '@cae/cobalt-react/enums';
import { useMediaQuery } from '@cae/cobalt-react/hooks';
import { CobaltIconLogOutSolid } from '@cae/cobalt-react/icons/solid/CobaltIconLogOutSolid';
import { useAuthenticatedUser } from '@contexts/auth-provider';
import { useModules } from '@contexts/modules-provider';
import { AppLoader } from '@shared/components/app-loader/AppLoader';
import { getSalutation } from '@shared/helpers/get-salutation/getSalutation';
import { useLogout } from '@shared/hooks/use-logout/useLogout';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';

import styles from './ModuleSelectorPage.module.css';
import { MODULE_ORDER, MODULE_CONTENT } from './ModuleSelectorPage.utils';

const ModuleSelectorPage = React.memo((): React.ReactElement => {
  const navigate = useNavigate();
  const { modules, selectModule, isModulesLoading } = useModules();
  const [isSelecting, setIsSelecting] = React.useState(false);
  const authData = useAuthenticatedUser();
  const logout = useLogout();

  const isMobileAndTablet = useMediaQuery(MediaQuery.MobileAndTablet);
  const titleId = 'module-selector-title';

  const salutation = React.useMemo(() => getSalutation(), []);

  const sortedModules = React.useMemo((): Module[] => {
    return MODULE_ORDER.reduce<Module[]>((acc, name) => {
      const module = modules.find(m => m.name === name);
      if (module) acc.push(module);
      return acc;
    }, []);
  }, [modules]);

  const handleModuleSelect = React.useCallback(
    async (moduleName: string): Promise<void> => {
      const module = modules?.find(m => m.name === moduleName);
      if (!module) return;

      setIsSelecting(true);
      try {
        await selectModule(module.name);
        navigate('/app-initializer', { replace: true });
      } catch (error) {
        console.error('Error selecting module:', error);
        setIsSelecting(false);
      }
    },
    [modules, selectModule, navigate]
  );

  if (isModulesLoading || isSelecting) {
    return <AppLoader />;
  }

  return (
    <div
      className={styles.moduleSelectorPage}
      data-testid="module-selector-page"
    >
      <img
        src={swirlTopLeft}
        alt=""
        aria-hidden="true"
        className={styles.swirlTopLeft}
      />
      <img
        src={swirlBottomRight}
        alt=""
        aria-hidden="true"
        className={styles.swirlBottomRight}
      />

      <div
        className={styles.moduleSelectorHeader}
        data-testid="module-selector-header"
      >
        <CobaltButton variant="ghost" onClick={logout}>
          Log out
          <CobaltIconLogOutSolid />
        </CobaltButton>
      </div>

      <section
        className={styles.moduleSelectorContent}
        data-testid="module-selector-content"
        aria-labelledby={titleId}
      >
        <div className={styles.moduleSelectorContentHeader}>
          {!isMobileAndTablet && (
            <>
              <h1 className="heading-04" id={titleId}>
                {salutation}, {authData.firstName}!
                <br />
                Which{' '}
                <span className={styles.moduleSelectorHighlight}>
                  Connect experience
                </span>{' '}
                are you looking for?
              </h1>

              <p className="body-02">
                You can switch between your roles in the app anytime.
              </p>
            </>
          )}
          {isMobileAndTablet && (
            <>
              <h1 className="heading-03" id={titleId}>
                {salutation}, <br />
                {authData.firstName}!
              </h1>
              <h2 className="heading-01">
                Which{' '}
                <span className={styles.moduleSelectorHighlight}>
                  Connect experience
                </span>
                <br />
                are you looking for?
              </h2>
            </>
          )}
        </div>

        <div className={styles.moduleSelectorTiles}>
          {sortedModules.map(module => {
            const content = MODULE_CONTENT[module.name] || {
              title: module.name,
              description: '',
            };
            return (
              <CobaltClickableTile
                key={module.name}
                onClick={() => handleModuleSelect(module.name)}
                className={styles.moduleTile}
                id={module.name}
              >
                <div className={styles.moduleTileContent}>
                  <div className={styles.moduleTileHeader}>
                    <h2
                      className={
                        isMobileAndTablet ? 'heading-01' : 'heading-02'
                      }
                    >
                      {content.title}
                    </h2>
                    {content.note && (
                      <p className={`${styles.moduleNote} body-01`}>
                        {content.note}
                      </p>
                    )}
                  </div>

                  <p className={isMobileAndTablet ? 'body-01' : 'body-02'}>
                    {content.description}
                  </p>
                </div>
              </CobaltClickableTile>
            );
          })}
        </div>
      </section>

      {isMobileAndTablet && (
        <p className={`body-01 ${styles.moduleSelectorMobileFooter}`}>
          You can switch between your roles in the app anytime.
        </p>
      )}
    </div>
  );
});

ModuleSelectorPage.displayName = 'ModuleSelectorPage';

export { ModuleSelectorPage };
